import type { Locale } from "@/preferences"
import cardzoneCodashopLogo from "../public/clients/cardzone-codashop.png"
import cardzoneDaleelLogo from "../public/clients/cardzone-daleel-store.png"
import cardzoneEnebaLogo from "../public/clients/cardzone-eneba.png"
import cardzoneOffgamersLogo from "../public/clients/cardzone-offgamers.png"
import cardzoneRazerGoldLogo from "../public/clients/cardzone-razer-gold.png"
import cardzoneSallaLogo from "../public/clients/cardzone-salla.svg"
import cardzoneSeagmLogo from "../public/clients/cardzone-seagm.png"
import blancoLogo from "../public/clients/blanco.png"
import govyyLogo from "../public/clients/govyy.png"
import osmaLogo from "../public/clients/osma.png"
import reefLogo from "../public/clients/reef.webp"
import taybatLogo from "../public/clients/taybat.png"

export interface SiteCopy {
  meta: {
    title: string
    description: string
  }
  accessibility: {
    skip: string
    menu: string
    language: string
    theme: string
    light: string
    dark: string
    openExternal: string
  }
  nav: {
    work: string
    openSource: string
    contact: string
  }
  hero: {
    title: string
    titleAccent: string
    description: string
    explore: string
    source: string
    visualLabel: string
  }
  about: {
    eyebrow: string
    title: string
    description: string
    tags: string[]
  }
  work: {
    eyebrow: string
    title: string
    description: string
    visit: string
    trusted: string
    network: string
    before: string
    after: string
    properties: {
      index: string
      category: string
      name: string
      tagline: string
      description: string
      detail: string
    }
    cardzone: {
      index: string
      category: string
      name: string
      tagline: string
      description: string
      detail: string
      status: string
    }
    bousla: {
      index: string
      category: string
      name: string
      tagline: string
      description: string
      detail: string
      metric: string
      metricLabel: string
    }
  }
  openSource: {
    eyebrow: string
    title: string
    description: string
    project: string
    projectDescription: string
    phase: string
    downloads: string
    packages: string
    cta: string
  }
  contact: {
    eyebrow: string
    title: string
    emailLabel: string
    whatsappLabel: string
  }
  footer: {
    statement: string
    work: string
    source: string
    contact: string
    rights: string
    top: string
  }
}

export const copy: Record<Locale, SiteCopy> = {
  en: {
    meta: {
      title: "The Amazing Team | Products worth noticing",
      description:
        "The Amazing Team builds focused technology products across property, commerce, and digital infrastructure.",
    },
    accessibility: {
      skip: "Skip to content",
      menu: "Primary navigation",
      language: "Switch to Arabic",
      theme: "Switch color theme",
      light: "Use light mode",
      dark: "Use dark mode",
      openExternal: "opens in a new tab",
    },
    nav: {
      work: "Our work",
      openSource: "Open source",
      contact: "Contact",
    },
    hero: {
      title: "Different products.",
      titleAccent: "One amazing standard.",
      description:
        "We build focused technology businesses across property, commerce, and digital infrastructure. Designed for the region and engineered to last.",
      explore: "Explore our work",
      source: "See open source",
      visualLabel: "An asymmetric folded prism in continuous motion",
    },
    about: {
      eyebrow: "What we are",
      title: "A small team for ambitious, useful things.",
      description:
        "We find stubborn real-world problems, build the technology around them, and stay close enough to make the details count.",
      tags: ["Property", "Commerce", "Infrastructure", "Open source"],
    },
    work: {
      eyebrow: "Current projects",
      title: "Built as products, not presentations.",
      description:
        "Each venture has its own world, customers, and craft. They share one obsession: make the complicated feel remarkably clear.",
      visit: "Visit project",
      trusted: "Trusted By",
      network: "Partners & Network",
      before: "Before",
      after: "After",
      properties: {
        index: "01",
        category: "Property",
        name: "Properties",
        tagline: "Old buildings. New chapters.",
        description:
          "Properties transforms aging buildings into modern residential spaces through long-term leases, comprehensive renovation, and professional operation.",
        detail:
          "Reliable rental income for owners, better homes for residents, and stronger long-term value for every property.",
      },
      cardzone: {
        index: "02",
        category: "Digital commerce infrastructure",
        name: "Cardzone",
        tagline: "The digital card network for stores.",
        description:
          "Cardzone connects merchants to thousands of gaming top-ups, gift cards, and vouchers with instant fulfillment and smart multi-provider routing.",
        detail:
          "One managed catalog, resilient delivery, and integrations for Salla and B2B APIs.",
        status: "Fulfilled",
      },
      bousla: {
        index: "03",
        category: "Commerce intelligence",
        name: "Bousla",
        tagline: "The smart growth platform.",
        description:
          "Bousla brings real-time analytics, AI-powered answers, smart inventory, and advanced marketing tools into one command center for online stores.",
        detail:
          "Understand customers, attribute campaigns, sync audiences, and act on what the numbers are saying.",
        metric: "LIVE",
        metricLabel: "Store intelligence",
      },
    },
    openSource: {
      eyebrow: "Open source",
      title: "Useful work should travel.",
      description:
        "We maintain software that helps other builders move faster because strong infrastructure gets better when it is shared.",
      project: "ZATCA Ruby SDK",
      projectDescription:
        "An open-source Ruby SDK for generating QR codes and e-invoices according to Saudi Arabia’s ZATCA standard, with support for Phase 1 and Phase 2.",
      phase: "Phase 1 + Phase 2",
      downloads: "combined downloads",
      packages: "zatca + zatca-sdk",
      cta: "View on GitHub",
    },
    contact: {
      eyebrow: "Contact us",
      title: "Have a useful problem? Let’s talk.",
      emailLabel: "Email us",
      whatsappLabel: "Chat on WhatsApp",
    },
    footer: {
      statement: "Making useful things feel amazing.",
      work: "Products",
      source: "GitHub",
      contact: "Contact",
      rights: "The Amazing Team. All rights reserved.",
      top: "Back to top",
    },
  },
  ar: {
    meta: {
      title: "The Amazing Team | منتجات تستحق الانتباه",
      description:
        "يبني The Amazing Team منتجات تقنية مركّزة في العقار والتجارة والبنية الرقمية.",
    },
    accessibility: {
      skip: "انتقل إلى المحتوى",
      menu: "التنقل الرئيسي",
      language: "التبديل إلى الإنجليزية",
      theme: "تبديل مظهر الألوان",
      light: "استخدام الوضع الفاتح",
      dark: "استخدام الوضع الداكن",
      openExternal: "يفتح في نافذة جديدة",
    },
    nav: {
      work: "مشاريعنا",
      openSource: "المصدر المفتوح",
      contact: "تواصل معنا",
    },
    hero: {
      title: "منتجات مختلفة.",
      titleAccent: "معيار رائع واحد.",
      description:
        "نبني شركات تقنية مركّزة في العقار والتجارة والبنية الرقمية؛ مصممة للمنطقة ومبنية لتدوم.",
      explore: "اكتشف مشاريعنا",
      source: "شاهد المصدر المفتوح",
      visualLabel: "مجسّم مطوي غير متماثل في حركة مستمرة",
    },
    about: {
      eyebrow: "من نحن",
      title: "فريق صغير لأفكار طموحة ومفيدة.",
      description:
        "نبحث عن المشكلات الواقعية العنيدة، ونبني التقنية التي تحلها، ونبقى قريبين بما يكفي لنعتني بكل تفصيلة.",
      tags: ["العقار", "التجارة", "البنية الرقمية", "المصدر المفتوح"],
    },
    work: {
      eyebrow: "مشاريعنا الحالية",
      title: "منتجات حقيقية، وليست مجرد عروض.",
      description:
        "لكل مشروع عالمه وعملاؤه وحرفته الخاصة. ويجمعها هوس واحد: أن نجعل المعقّد واضحاً بصورة لافتة.",
      visit: "زيارة المشروع",
      trusted: "موثوق من قبل",
      network: "الشركاء والشبكة",
      before: "قبل",
      after: "بعد",
      properties: {
        index: "٠١",
        category: "العقار",
        name: "العقارات الرائعة",
        tagline: "مبانٍ قديمة. فصول جديدة.",
        description:
          "تحوّل العقارات الرائعة المباني القديمة إلى وحدات سكنية عصرية عبر عقود طويلة الأجل، وتجديد شامل، وإدارة محترفة.",
        detail:
          "دخل إيجاري موثوق للملاك، ومساكن أفضل للسكان، وقيمة أقوى لكل عقار على المدى الطويل.",
      },
      cardzone: {
        index: "٠٢",
        category: "بنية التجارة الرقمية",
        name: "كاردزون",
        tagline: "شبكة البطاقات الرقمية للمتاجر.",
        description:
          "تربط كاردزون المتاجر بآلاف بطاقات شحن الألعاب وبطاقات الهدايا والقسائم، مع تنفيذ فوري وتوجيه ذكي بين المزوّدين.",
        detail:
          "كتالوج واحد مُدار، وتسليم مرن، وتكامل مع سلة وواجهات B2B البرمجية.",
        status: "تم التنفيذ",
      },
      bousla: {
        index: "٠٣",
        category: "ذكاء التجارة",
        name: "بوصلة",
        tagline: "منصة النمو الذكية.",
        description:
          "تجمع بوصلة التحليلات الفورية، وإجابات الذكاء الاصطناعي، وإدارة المخزون الذكية، وأدوات التسويق المتقدمة في مركز قيادة واحد للمتاجر الإلكترونية.",
        detail:
          "افهم عملاءك، وانسب نتائج الحملات، وزامن الجماهير، وتصرّف بناءً على ما تقوله الأرقام.",
        metric: "مباشر",
        metricLabel: "ذكاء المتجر",
      },
    },
    openSource: {
      eyebrow: "المصدر المفتوح",
      title: "العمل المفيد يستحق أن ينتشر.",
      description:
        "نعتني ببرمجيات تساعد المطورين الآخرين على التحرك أسرع؛ لأن البنية القوية تصبح أفضل عندما تُشارك.",
      project: "Ruby SDK للفوترة الإلكترونية",
      projectDescription:
        "حزمة Ruby مفتوحة المصدر لإنشاء رموز QR والفواتير الإلكترونية وفق معيار هيئة الزكاة والضريبة والجمارك في السعودية، مع دعم المرحلتين الأولى والثانية.",
      phase: "المرحلة الأولى + الثانية",
      downloads: "إجمالي التنزيلات",
      packages: "zatca + zatca-sdk",
      cta: "عرض المشروع على GitHub",
    },
    contact: {
      eyebrow: "تواصل معنا",
      title: "لديك مشكلة تستحق الحل؟ لنتحدث.",
      emailLabel: "راسلنا",
      whatsappLabel: "تواصل معنا عبر واتساب",
    },
    footer: {
      statement: "نصنع أشياء مفيدة بطابع رائع.",
      work: "المنتجات",
      source: "GitHub",
      contact: "تواصل معنا",
      rights: "The Amazing Team. جميع الحقوق محفوظة.",
      top: "العودة إلى الأعلى",
    },
  },
}

export const bouslaClients = [
  {
    name: "Reef",
    image: reefLogo,
    href: "https://reefperfumes.com/ar",
  },
  {
    name: "GOVYY",
    image: govyyLogo,
    href: "https://govyy.com",
  },
  {
    name: "Blanco",
    image: blancoLogo,
    href: "https://blancoparfumes.com/ar",
  },
  {
    name: "Osma Perfumes",
    image: osmaLogo,
    href: "https://osmaperfumes.com/en",
  },
  {
    name: "Taybat",
    image: taybatLogo,
    href: "https://taybat.sa",
  },
] as const

export const cardzonePartners = [
  {
    name: "Salla",
    image: cardzoneSallaLogo,
    href: "https://apps.salla.sa/en/app/1469240980",
  },
  { name: "Codashop", image: cardzoneCodashopLogo },
  { name: "SEAGM", image: cardzoneSeagmLogo },
  { name: "Razer Gold", image: cardzoneRazerGoldLogo },
  { name: "OffGamers", image: cardzoneOffgamersLogo },
  { name: "Eneba", image: cardzoneEnebaLogo },
  { name: "Daleel Store", image: cardzoneDaleelLogo },
] as const

export const projectLinks = {
  properties: "https://properties.theamazingteam.com",
  cardzone: "https://cardzone.ai",
  bousla: "https://bousla.com",
  zatca: "https://github.com/theamazingteam/zatca",
  github: "https://github.com/theamazingteam",
} as const
